/**
 * DoN Care Plan Review Queue (DRR-220 Phase 4)
 *
 * Director of Nursing reviews USRN-approved care plans with full deanonymized
 * context before final signature. This is the terminal approval step.
 *
 * Access control:
 *   - RequireScope("clinical:full") on backend
 *   - Nav group: admin (DoN is facility admin)
 *   - Deanonymized data (real resident names)
 *
 * Workflow:
 *   LPN assessment → Claude draft → USRN review (tokenized) → **DoN finalize**
 *
 * Legal basis (MN):
 *   - 144G.70: Service plan requires RN signature
 *   - 148.171 Subd 2: RN scope of practice includes care planning
 *   - This is the CLINICAL JUDGMENT step
 */

import { useState, useCallback } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Alert,
  Button,
  Group,
  Badge,
  Card,
  Divider,
  LoadingOverlay,
  Modal,
  Textarea,
  SimpleGrid,
  ThemeIcon,
  ScrollArea,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconUser,
  IconFileText,
  IconSignature,
  IconClock,
  IconNotes,
  IconRefresh,
  IconChevronRight,
} from '@tabler/icons-react';
// eslint-disable-next-line no-restricted-imports -- Data layer hook requires direct Apollo access
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';

import { useTenantId, useViewLayer } from '@/auth';
import { PriorCarePlanContext } from './PriorCarePlanContext';

// Status constants (matches backend CarePlanGenerationStatus)
// Defined locally until codegen regenerates from updated schema
// Using const object to satisfy DRR-074 (no hardcoded status strings)
const CarePlanStatus = {
  PendingGeneration: 'PENDING_GENERATION',
  PendingUsrnReview: 'PENDING_USRN_REVIEW',
  UsrnChangesRequested: 'USRN_CHANGES_REQUESTED',
  PendingDonReview: 'PENDING_DON_REVIEW',
  DonRejected: 'DON_REJECTED',
  Finalized: 'FINALIZED',
} as const;

type CarePlanGenerationStatus = typeof CarePlanStatus[keyof typeof CarePlanStatus];

// =============================================================================
// GraphQL
// =============================================================================

const GET_DON_CARE_PLAN_QUEUE = gql`
  query GetDonCarePlanQueue {
    donCarePlanQueue {
      generationId
      status
      createdAt
      residentName
      residentActorId
      clinicalReviewerNotes
      carePlan
      usrnDecidedAt
    }
  }
`;

const FINALIZE_CARE_PLAN = gql`
  mutation FinalizeCarePlan($generationId: String!, $donNotes: String!, $approved: Boolean!) {
    finalizeCarePlan(generationId: $generationId, donNotes: $donNotes, approved: $approved) {
      success
      generationId
      message
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

interface DonQueueItem {
  generationId: string;
  status: CarePlanGenerationStatus;
  createdAt: string;
  residentName: string;
  residentActorId: string;
  clinicalReviewerNotes: string;
  carePlan: string;
  usrnDecidedAt: string | null;
}

// =============================================================================
// Status Badge
// =============================================================================

function StatusBadge({ status }: { status: CarePlanGenerationStatus }) {
  const config: Record<CarePlanGenerationStatus, { color: string; label: string }> = {
    [CarePlanStatus.PendingGeneration]: { color: 'gray', label: 'Generating' },
    [CarePlanStatus.PendingUsrnReview]: { color: 'blue', label: 'USRN Review' },
    [CarePlanStatus.UsrnChangesRequested]: { color: 'orange', label: 'Changes Requested' },
    [CarePlanStatus.PendingDonReview]: { color: 'yellow', label: 'Awaiting Your Review' },
    [CarePlanStatus.Finalized]: { color: 'green', label: 'Approved & Signed' },
    [CarePlanStatus.DonRejected]: { color: 'red', label: 'Rejected' },
  };

  const { color, label } = config[status] ?? { color: 'gray', label: status };

  return <Badge color={color} variant="light">{label}</Badge>;
}

// =============================================================================
// Queue Item Card
// =============================================================================

function QueueItemCard({
  item,
  onSelect,
  selected,
}: {
  item: DonQueueItem;
  onSelect: () => void;
  selected: boolean;
}) {
  const createdDate = new Date(item.createdAt);
  const waitingDays = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

  return (
    <Card
      shadow={selected ? 'md' : 'sm'}
      padding="md"
      radius="md"
      withBorder
      style={{
        cursor: 'pointer',
        borderColor: selected ? 'var(--mantine-color-blue-5)' : undefined,
        backgroundColor: selected ? 'var(--mantine-color-blue-0)' : undefined,
      }}
      onClick={onSelect}
    >
      <Group justify="space-between" mb="xs">
        <Group gap="sm">
          <ThemeIcon size="md" variant="light" color="blue">
            <IconUser size={16} />
          </ThemeIcon>
          <Text fw={600}>{item.residentName}</Text>
        </Group>
        <StatusBadge status={item.status} />
      </Group>

      <Text size="sm" c="dimmed" mb="xs">
        ID: {item.residentActorId}
      </Text>

      <Divider my="xs" />

      <Group justify="space-between">
        <Group gap="xs">
          <IconClock size={14} />
          <Text size="xs" c="dimmed">
            Waiting {waitingDays} day{waitingDays !== 1 ? 's' : ''}
          </Text>
        </Group>
        {item.usrnDecidedAt && (
          <Tooltip label={`USRN approved: ${new Date(item.usrnDecidedAt).toLocaleString()}`}>
            <Badge size="xs" variant="dot" color="green">USRN Approved</Badge>
          </Tooltip>
        )}
      </Group>
    </Card>
  );
}

// =============================================================================
// Review Panel
// =============================================================================

function ReviewPanel({
  item,
  onFinalize,
  loading,
}: {
  item: DonQueueItem;
  onFinalize: (approved: boolean, notes: string) => void;
  loading: boolean;
}) {
  const [notes, setNotes] = useState('');
  const [confirmModal, setConfirmModal] = useState<'approve' | 'reject' | null>(null);

  const handleFinalize = useCallback(() => {
    if (!confirmModal) return;
    onFinalize(confirmModal === 'approve', notes);
    setConfirmModal(null);
  }, [confirmModal, notes, onFinalize]);

  return (
    <Stack gap="md">
      <LoadingOverlay visible={loading} />

      {/* Header */}
      <Paper p="md" withBorder>
        <Group justify="space-between">
          <Group gap="md">
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconUser size={20} />
            </ThemeIcon>
            <div>
              <Text fw={600} size="lg">{item.residentName}</Text>
              <Text size="sm" c="dimmed">ID: {item.residentActorId}</Text>
            </div>
          </Group>
          <StatusBadge status={item.status} />
        </Group>
      </Paper>

      {/* Prior Care Plan Context (90-day reassessment per MN 144G.70) */}
      <PriorCarePlanContext
        residentActorId={item.residentActorId}
        generationId={item.generationId}
        mode="full"
      />

      {/* USRN Notes */}
      <Paper p="md" withBorder>
        <Group gap="xs" mb="sm">
          <ThemeIcon size="sm" variant="light" color="teal">
            <IconNotes size={14} />
          </ThemeIcon>
          <Text fw={500} size="sm">Clinical Reviewer Notes</Text>
        </Group>
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {item.clinicalReviewerNotes || '(No notes provided)'}
        </Text>
      </Paper>

      {/* Care Plan Content */}
      <Paper p="md" withBorder>
        <Group gap="xs" mb="sm">
          <ThemeIcon size="sm" variant="light" color="violet">
            <IconFileText size={14} />
          </ThemeIcon>
          <Text fw={500} size="sm">Care Plan</Text>
        </Group>
        <ScrollArea h={300}>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {item.carePlan}
          </Text>
        </ScrollArea>
      </Paper>

      {/* DoN Notes */}
      <Paper p="md" withBorder>
        <Group gap="xs" mb="sm">
          <ThemeIcon size="sm" variant="light" color="orange">
            <IconSignature size={14} />
          </ThemeIcon>
          <Text fw={500} size="sm">Your Clinical Notes (Required)</Text>
        </Group>
        <Textarea
          placeholder="Enter your clinical assessment and any modifications made..."
          minRows={4}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          disabled={item.status !== CarePlanStatus.PendingDonReview}
        />
      </Paper>

      {/* Actions */}
      {item.status === CarePlanStatus.PendingDonReview && (
        <Group justify="flex-end" gap="md">
          <Button
            variant="outline"
            color="red"
            leftSection={<IconX size={16} />}
            onClick={() => setConfirmModal('reject')}
            disabled={notes.length < 10}
          >
            Reject & Return
          </Button>
          <Button
            color="green"
            leftSection={<IconSignature size={16} />}
            onClick={() => setConfirmModal('approve')}
            disabled={notes.length < 10}
          >
            Approve & Sign
          </Button>
        </Group>
      )}

      {notes.length < 10 && item.status === CarePlanStatus.PendingDonReview && (
        <Text size="xs" c="dimmed" ta="right">
          Notes must be at least 10 characters
        </Text>
      )}

      {/* Confirmation Modal */}
      <Modal
        opened={confirmModal !== null}
        onClose={() => setConfirmModal(null)}
        title={confirmModal === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            {confirmModal === 'approve' ? (
              <>
                You are about to <strong>approve and sign</strong> this care plan for{' '}
                <strong>{item.residentName}</strong>. This action is permanent and will
                create the official care plan record.
              </>
            ) : (
              <>
                You are about to <strong>reject</strong> this care plan for{' '}
                <strong>{item.residentName}</strong>. The care plan will be returned
                for re-assessment.
              </>
            )}
          </Text>

          <Alert
            color={confirmModal === 'approve' ? 'green' : 'red'}
            title="Legal Notice"
          >
            <Text size="xs">
              {confirmModal === 'approve' ? (
                'By approving, you are signing this care plan as required by MN 144G.70. Your signature certifies that the plan meets all regulatory requirements.'
              ) : (
                'Rejection will create an audit record. The resident will need a new assessment before a care plan can be finalized.'
              )}
            </Text>
          </Alert>

          <Group justify="flex-end" gap="md">
            <Button variant="outline" onClick={() => setConfirmModal(null)}>
              Cancel
            </Button>
            <Button
              color={confirmModal === 'approve' ? 'green' : 'red'}
              onClick={handleFinalize}
            >
              {confirmModal === 'approve' ? 'Approve & Sign' : 'Reject'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// =============================================================================
// Main Page
// =============================================================================

export function DonReviewPage() {
  const _tenantId = useTenantId(); // Reserved for multi-tenant support
  const viewLayer = useViewLayer();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // DoN is admin-level role. Backend enforces clinical:full scope.
  // Frontend shows access denied for non-admin users as a UX hint.
  const hasClinicalFull = viewLayer === 'admin';

  // Fetch queue
  const { data, loading, error, refetch } = useQuery(GET_DON_CARE_PLAN_QUEUE, {
    skip: !hasClinicalFull,
    pollInterval: 30000, // Refresh every 30 seconds
  });

  // Finalize mutation
  const [finalizeCarePlan, { loading: finalizing }] = useMutation(FINALIZE_CARE_PLAN, {
    onCompleted: () => {
      refetch();
      setSelectedId(null);
    },
  });

  const queueItems: DonQueueItem[] = data?.donCarePlanQueue ?? [];
  const selectedItem = queueItems.find((item) => item.generationId === selectedId);

  const handleFinalize = useCallback(
    (approved: boolean, notes: string) => {
      if (!selectedId) return;
      finalizeCarePlan({
        variables: {
          generationId: selectedId,
          donNotes: notes,
          approved,
        },
      });
    },
    [selectedId, finalizeCarePlan]
  );

  // Access denied
  if (!hasClinicalFull) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Access Denied" icon={<IconAlertTriangle />}>
          <Text>
            This page requires <strong>clinical:full</strong> scope. Only the Director
            of Nursing or equivalent clinical leadership can access care plan finalization.
          </Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Care Plan Review Queue</Title>
            <Text c="dimmed" size="sm">
              Review and finalize USRN-approved care plans
            </Text>
          </div>
          <Button
            variant="subtle"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>

        {/* Error state */}
        {error && (
          <Alert color="red" title="Error Loading Queue">
            {error.message}
          </Alert>
        )}

        {/* Empty state */}
        {!loading && queueItems.length === 0 && (
          <Paper p="xl" withBorder ta="center">
            <ThemeIcon size="xl" variant="light" color="green" mx="auto" mb="md">
              <IconCheck size={24} />
            </ThemeIcon>
            <Title order={4}>All Caught Up</Title>
            <Text c="dimmed" size="sm">
              No care plans awaiting your review
            </Text>
          </Paper>
        )}

        {/* Main content */}
        {queueItems.length > 0 && (
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {/* Queue list */}
            <Stack gap="sm">
              <Text fw={500} size="sm" c="dimmed">
                {queueItems.length} plan{queueItems.length !== 1 ? 's' : ''} pending review
              </Text>
              <LoadingOverlay visible={loading} />
              {queueItems.map((item) => (
                <QueueItemCard
                  key={item.generationId}
                  item={item}
                  onSelect={() => setSelectedId(item.generationId)}
                  selected={selectedId === item.generationId}
                />
              ))}
            </Stack>

            {/* Review panel */}
            <div>
              {selectedItem ? (
                <ReviewPanel
                  item={selectedItem}
                  onFinalize={handleFinalize}
                  loading={finalizing}
                />
              ) : (
                <Paper p="xl" withBorder ta="center" h="100%">
                  <Stack justify="center" h="100%" align="center">
                    <ThemeIcon size="xl" variant="light" color="gray">
                      <IconChevronRight size={24} />
                    </ThemeIcon>
                    <Text c="dimmed">Select a care plan to review</Text>
                  </Stack>
                </Paper>
              )}
            </div>
          </SimpleGrid>
        )}
      </Stack>
    </Container>
  );
}

export default DonReviewPage;
